import React, { useEffect, useRef } from "react";

import { Bold, Italic, List, ListOrdered, Underline } from "lucide-react";

import "./style.scss";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  ariaLabel?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  ariaLabel = "Email content",
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const skipSync = useRef(false);

  useEffect(() => {
    const node = editorRef.current;
    if (!node || skipSync.current) {
      skipSync.current = false;
      return;
    }
    if (node.innerHTML !== value) {
      node.innerHTML = value;
    }
  }, [value]);

  const emitChange = () => {
    skipSync.current = true;
    onChange(editorRef.current?.innerHTML ?? "");
  };

  const exec = (command: string, arg?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitChange();
  };

  return (
    <div className="rich-text-editor">
      <div className="rich-text-editor__toolbar">
        <button
          type="button"
          className="rich-text-editor__tool"
          title="Bold"
          aria-label="Bold"
          onMouseDown={event => event.preventDefault()}
          onClick={() => exec("bold")}
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          className="rich-text-editor__tool"
          title="Italic"
          aria-label="Italic"
          onMouseDown={event => event.preventDefault()}
          onClick={() => exec("italic")}
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          className="rich-text-editor__tool"
          title="Underline"
          aria-label="Underline"
          onMouseDown={event => event.preventDefault()}
          onClick={() => exec("underline")}
        >
          <Underline size={16} />
        </button>
        <button
          type="button"
          className="rich-text-editor__tool"
          title="Bulleted list"
          aria-label="Bulleted list"
          onMouseDown={event => event.preventDefault()}
          onClick={() => exec("insertUnorderedList")}
        >
          <List size={16} />
        </button>
        <button
          type="button"
          className="rich-text-editor__tool"
          title="Numbered list"
          aria-label="Numbered list"
          onMouseDown={event => event.preventDefault()}
          onClick={() => exec("insertOrderedList")}
        >
          <ListOrdered size={16} />
        </button>
      </div>
      <div
        ref={editorRef}
        className="rich-text-editor__surface"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        onInput={emitChange}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
